import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { ButtonComponent } from '@libs/common/components';
import { DialogService } from '@libs/shared/common';
import { TextComponent } from '@libs/form/components';
import { SubmitItem, TextInputModel } from '@libs/form/models';
import { ShoppingListModel } from '@libs/shop/models';
import { ShoppingListsState, ViewportService } from '@libs/shared/services';
import { firstValueFrom } from 'rxjs';
import {
  createSubmitItemData,
  dialogTitleEdit,
  dialogTitleNew,
  editSubmitItemData,
  nameFieldData,
} from './create-and-edit-shopping-list-dialog.model';

@Component({
  selector: 'ui-create-and-edit-shopping-list-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './create-and-edit-shopping-list-dialog.component.html',
  styleUrls: ['./create-and-edit-shopping-list-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    ButtonComponent,
    TextComponent,
    ReactiveFormsModule,
  ],
})
export class CreateAndEditShoppingListDialogComponent {
  @ViewChild('dialogContent', { read: TemplateRef })
  public dialogContentRef!: TemplateRef<unknown>;

  public readonly shoppingListState: ShoppingListsState = inject(ShoppingListsState);

  public shoppingListToEdit: InputSignal<ShoppingListModel | undefined> = input<ShoppingListModel | undefined>(undefined);
  public isMobile: Signal<boolean>;

  public lists: Signal<ShoppingListModel[]> = this.shoppingListState.entities;

  public readonly title: Signal<string> = computed(() => this.shoppingListToEdit() ? dialogTitleEdit : dialogTitleNew);
  public readonly nameFieldItem: Signal<TextInputModel> = signal(nameFieldData);
  public readonly submitItems: Signal<SubmitItem[]> = computed(() => this.shoppingListToEdit() ? editSubmitItemData.items : createSubmitItemData.items);

  public readonly nameFormControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

  private readonly dialog: DialogService = inject(DialogService);
  private dialogRef?: MatDialogRef<unknown>;
  private readonly viewportService: ViewportService = inject(ViewportService);

  public constructor() {
    this.isMobile = toSignal(this.viewportService.isMobile(), { initialValue: false });
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async openDialog(): Promise<MatDialogRef<unknown>> {
    if (this.shoppingListToEdit()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.nameFormControl.setValue(this.shoppingListToEdit()!.name);
    } else {
      this.nameFormControl.setValue('');
    }

    this.dialogRef = this.dialog.open(this.dialogContentRef, {
      panelClass: 'flex-dialog-container',
      restoreFocus: false,
      position: {
        top: this.isMobile() ? 'calc(15vh + env(safe-area-inset-top))' : undefined,
      },
    });

    void firstValueFrom(this.dialogRef.afterOpened())
      .then(() => {
        this.nameFormControl.addValidators(this.validateName.bind(this));
        this.nameFormControl.updateValueAndValidity();
      });

    void firstValueFrom(this.dialogRef.afterClosed())
      .then(() => {
        this.nameFormControl.reset();
      });

    return this.dialogRef;
  }

  public onAction(item: SubmitItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.name !== 'submit') {
      this.dialogRef?.close();

      return;
    }

    this.nameFormControl.markAllAsTouched();
    this.nameFormControl.updateValueAndValidity();

    if (!this.nameFormControl.valid) {
      return;
    }

    this.save();
  }

  private save(): void {
    let promise: Promise<unknown>;

    if (!this.shoppingListToEdit()) {
      promise = this.shoppingListState.createNewShoppingList(this.nameFormControl.value);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const list: ShoppingListModel = this.shoppingListToEdit()!;

      list.name = this.nameFormControl.value;
      promise = this.shoppingListState.updateShoppingList(list);
    }

    void promise.then(() => {
      this.dialogRef?.close();
    });
  }

  private validateName(control: AbstractControl): ValidationErrors | null {
    let lists: ShoppingListModel[] = this.lists();

    if (this.shoppingListToEdit()) {
      lists = lists.filter(x => x.id !== this.shoppingListToEdit()?.id);
    }

    const isNameTaken: boolean = lists.some(
      shoppingList => shoppingList.name === control.value,
    );

    return isNameTaken ? { nameTaken: true } : null;
  }
}
